/**
 * Sawyer C1 — "Additional services" on the Quick Book card.
 *
 * Sawyer (memo2): "you select the main service up top and then on that same
 * QuickBook card, you have additional services … bath [sic: 'bass' in the
 * transcript], the hiking". Johnny, on the call: "whatever services you have
 * turned on will show up". So the list is the SERVER's — what Settings has on
 * (active add-ons + services that do not put the dog on the floor), priced by
 * the function the charge uses — never a hard-coded menu, never a client
 * price.
 *
 * Deploy window (AC15): the section appears only after the quote answers 200.
 * A 403/404 (the server predates the route, or this role may not quote)
 * renders nothing and reports `available=false`, so the card never sends
 * `additionalServices` to a server that would strip it silently.
 *
 * Five states: default (the checklist) · loading ("Loading services…") ·
 * empty (nothing set up — says who adds them) · error (Try again) · stale
 * (re-quoted whenever the date, end date, dogs or main service change; a tick
 * the new quote no longer offers is dropped WITH a one-line note).
 */
import { useEffect, useRef, useState } from 'react';
import {
  fetchQuickBookServiceOptions,
  type AdminDraftServiceOption,
  type AdminDraftServiceOptions,
} from '../../lib/quick-book-services-api.js';
import type { SelectedService } from '../../lib/quick-book-outcome.js';
import './BookingExtras.css';

export const SERVICES_QUOTE_DEBOUNCE_MS = 300;

export interface AdditionalServicesFieldProps {
  apiUrl: string;
  customerId: string;
  /** YYYY-MM-DD — the visit day or the drop-off day. */
  date: string;
  /** YYYY-MM-DD — boarding pick-up day (inclusive); null for a single day. */
  endDate: string | null;
  dogCount: number;
  /** The card's main service — the server leaves it out of the list. */
  mainServiceTypeId: string | null;
  value: readonly SelectedService[];
  onChange: (next: SelectedService[]) => void;
  /** true once the quote answered 200 — the only time the card may send services. */
  onAvailability: (available: boolean) => void;
  disabled?: boolean;
}

type QuoteStatus = 'loading' | 'absent' | 'error' | 'ready';

export function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** What a ticked option costs as chosen: the first day, or every day of the stay. */
export function optionCost(option: AdminDraftServiceOption, everyDay: boolean): number {
  return everyDay ? option.dayTotals.reduce((s, d) => s + d.totalCents, 0) : option.totalCents;
}

export function AdditionalServicesField({
  apiUrl,
  customerId,
  date,
  endDate,
  dogCount,
  mainServiceTypeId,
  value,
  onChange,
  onAvailability,
  disabled = false,
}: AdditionalServicesFieldProps): JSX.Element | null {
  const [status, setStatus] = useState<QuoteStatus>('loading');
  // The last quote the server gave: kept on screen while a re-quote is in
  // flight (stale-while-revalidate) so the list does not flash on every date
  // change; the "Updating prices…" line says it is being refreshed.
  const [quote, setQuote] = useState<AdminDraftServiceOptions | null>(null);
  // Once the server has said "not here" (403/404), later re-quotes stay
  // invisible too — no "Loading services…" flash on an older server.
  const everAbsent = useRef(false);
  const [dropped, setDropped] = useState<string[]>([]);
  const [nonce, setNonce] = useState(0);
  // The latest selection and callbacks, read when a quote lands without
  // re-running the quote when they change.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onAvailabilityRef = useRef(onAvailability);
  onAvailabilityRef.current = onAvailability;

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    const timer = window.setTimeout(() => {
      void fetchQuickBookServiceOptions(apiUrl, { customerId, date, endDate, dogCount, serviceTypeId: mainServiceTypeId })
        .then((res) => {
          if (cancelled) return;
          if (res.data) {
            const quote = res.data;
            everAbsent.current = false;
            setQuote(quote);
            setStatus('ready');
            onAvailabilityRef.current(true);
            // A tick survives a re-quote only while the server still offers it.
            const multiDay = quote.stayDates.length > 1;
            const kept: SelectedService[] = [];
            const gone: string[] = [];
            for (const s of valueRef.current) {
              const o = quote.options.find((x) => x.kind === s.kind && x.id === s.id);
              if (o) kept.push({ ...s, label: o.label, everyDay: multiDay ? s.everyDay : false });
              else gone.push(s.label);
            }
            setDropped(gone);
            const changed =
              kept.length !== valueRef.current.length ||
              kept.some((k, i) => k.everyDay !== valueRef.current[i]!.everyDay || k.label !== valueRef.current[i]!.label);
            if (changed) onChangeRef.current(kept);
            return;
          }
          if (res.status === 403 || res.status === 404 || res.unrecognised) {
            everAbsent.current = true;
            setQuote(null);
            setStatus('absent');
            onAvailabilityRef.current(false);
            if (valueRef.current.length > 0) onChangeRef.current([]);
            return;
          }
          setQuote(null);
          setStatus('error');
          onAvailabilityRef.current(false);
        });
    }, SERVICES_QUOTE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [apiUrl, customerId, date, endDate, dogCount, mainServiceTypeId, nonce]);

  if (status === 'absent' || (status === 'loading' && everAbsent.current)) return null;

  const label = <span className="ferro-quickbook__section-label">Additional services</span>;

  if (status === 'loading' && !quote) {
    return (
      <section className="ferro-quickbook__section" data-testid="quickbook-services">
        {label}
        <p className="ferro-quickbook__hint" role="status" data-testid="quickbook-services-state-loading">
          Loading services…
        </p>
      </section>
    );
  }

  if (status === 'error' || !quote) {
    return (
      <section className="ferro-quickbook__section" data-testid="quickbook-services">
        {label}
        <div className="ferro-quickbook__hint ferro-booking-extras__state" role="alert" data-testid="quickbook-services-state-error">
          <span>Couldn't load the service list. You can still book and add services from the visit.</span>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-booking-extras__retry"
            onClick={() => setNonce((n) => n + 1)}
            data-testid="quickbook-services-retry"
          >
            Try again
          </button>
        </div>
      </section>
    );
  }

  if (quote.options.length === 0) {
    return (
      <section className="ferro-quickbook__section" data-testid="quickbook-services">
        {label}
        <p className="ferro-quickbook__hint" data-testid="quickbook-services-state-empty">
          No additional services are set up yet. The owner adds them in Settings → Services &amp; prices.
        </p>
      </section>
    );
  }

  const multiDay = quote.stayDates.length > 1;
  const days = quote.stayDates.length;
  const toggle = (o: AdminDraftServiceOption, on: boolean) => {
    const rest = value.filter((s) => !(s.kind === o.kind && s.id === o.id));
    onChange(on ? [...rest, { kind: o.kind, id: o.id, label: o.label, everyDay: false }] : rest);
  };
  const setEveryDay = (o: AdminDraftServiceOption, everyDay: boolean) =>
    onChange(value.map((s) => (s.kind === o.kind && s.id === o.id ? { ...s, everyDay } : s)));

  const row = (o: AdminDraftServiceOption) => {
    const chosen = value.find((s) => s.kind === o.kind && s.id === o.id);
    const everyDay = chosen?.everyDay === true;
    const perDog = quote.dogCount > 1 && o.kind === 'addOn';
    return (
      <li key={`${o.kind}:${o.id}`}>
        <label className="ferro-booking-extras__row" data-checked={Boolean(chosen)} data-testid={`quickbook-service-${o.id}`}>
          <input
            type="checkbox"
            checked={Boolean(chosen)}
            disabled={disabled}
            onChange={(e) => toggle(o, e.target.checked)}
          />
          <span className="ferro-booking-extras__label">
            <span>{o.label}</span>
            {perDog ? (
              <span className="ferro-quickbook__hint">
                for {quote.dogCount} dogs ({money(Math.round(o.totalCents / quote.dogCount))} each)
              </span>
            ) : null}
          </span>
          <span className="ferro-booking-extras__price" data-testid={`quickbook-service-price-${o.id}`}>
            {money(optionCost(o, everyDay))}
          </span>
        </label>
        {chosen && multiDay ? (
          <fieldset className="ferro-booking-extras__choices" data-testid={`quickbook-service-everyday-${o.id}`}>
            <legend className="ferro-quickbook__hint">How often for {o.label}</legend>
            <label className="ferro-booking-extras__choice">
              <input
                type="radio"
                name={`qb-service-often-${o.id}`}
                checked={!everyDay}
                disabled={disabled}
                onChange={() => setEveryDay(o, false)}
                data-testid={`quickbook-service-once-${o.id}`}
              />
              <span>Once (drop-off day) — {money(o.totalCents)}</span>
            </label>
            <label className="ferro-booking-extras__choice">
              <input
                type="radio"
                name={`qb-service-often-${o.id}`}
                checked={everyDay}
                disabled={disabled}
                onChange={() => setEveryDay(o, true)}
                data-testid={`quickbook-service-every-${o.id}`}
              />
              <span>
                Every day of the stay ({days} days) — {money(optionCost(o, true))}
              </span>
            </label>
          </fieldset>
        ) : null}
      </li>
    );
  };

  const addOns = quote.options.filter((o) => o.kind === 'addOn');
  const services = quote.options.filter((o) => o.kind === 'service');

  return (
    <section className="ferro-quickbook__section" data-testid="quickbook-services">
      {label}
      {addOns.length > 0 ? (
        <>
          <span className="ferro-booking-extras__micro">Add-ons</span>
          <ul className="ferro-booking-extras__group">{addOns.map(row)}</ul>
        </>
      ) : null}
      {services.length > 0 ? (
        <>
          <span className="ferro-booking-extras__micro">Services</span>
          <ul className="ferro-booking-extras__group">{services.map(row)}</ul>
        </>
      ) : null}
      {status === 'loading' ? (
        <p className="ferro-quickbook__hint ferro-booking-extras__note" role="status" data-testid="quickbook-services-state-updating">
          Updating prices…
        </p>
      ) : null}
      {dropped.map((name) => (
        <p key={name} className="ferro-quickbook__hint ferro-booking-extras__note" role="status" data-testid="quickbook-services-dropped">
          {name} is not offered on the new date and was unticked.
        </p>
      ))}
    </section>
  );
}
